import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Header } from '../components/landing/Header';
import { Footer } from '../components/landing/Footer';
import { useInView } from '../hooks/useInView';
import { useEffect } from 'react';

const ScreenshotPlaceholder = ({ label }) => (
  <div className="w-full rounded-[12px] border border-stone-200/70 bg-[#f5f3ef] flex items-center justify-center min-h-[320px] lg:min-h-[420px]">
    <p className="text-[0.88rem] text-stone-400 font-medium text-center px-6 select-none">
      [ {label} ]
    </p>
  </div>
);

const Section = ({ title, placeholder, children, flipped }) => {
  const [ref, isInView] = useInView();

  return (
    <section className="py-[70px] lg:py-[90px]">
      <div
        ref={ref}
        className={`max-w-[1200px] mx-auto px-6 lg:px-8 ${
          isInView ? 'animate-fade-in-up' : 'opacity-0'
        }`}
      >
        <h2 className="font-heading text-[1.6rem] lg:text-[2rem] font-bold text-stone-900 leading-[1.2] tracking-[-0.01em] mb-8">
          {title}
        </h2>

        {flipped ? (
          <>
            <div className="mb-8">{children}</div>
            {placeholder && <ScreenshotPlaceholder label={placeholder} />}
          </>
        ) : (
          <>
            {placeholder && (
              <div className="mb-8">
                <ScreenshotPlaceholder label={placeholder} />
              </div>
            )}
            {children}
          </>
        )}
      </div>
    </section>
  );
};

const BulletList = ({ items }) => (
  <ul className="space-y-2 mt-4 mb-5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5">
        <span className="mt-[7px] w-[5px] h-[5px] rounded-full bg-stone-400 flex-shrink-0" />
        <span className="text-stone-600 text-[0.9rem] leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

const Paragraph = ({ children, className = '' }) => (
  <p className={`text-[0.95rem] text-stone-500 leading-[1.75] max-w-[680px] ${className}`}>
    {children}
  </p>
);

const Emphasis = ({ children }) => (
  <p className="text-[0.9rem] text-stone-400 leading-[1.7] max-w-[680px] mt-1">{children}</p>
);

export default function DemoPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [heroRef, heroInView] = useInView();

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <Header />

      <main>
        {/* Hero */}
        <section className="pt-[140px] pb-[60px] lg:pt-[160px] lg:pb-[70px]">
          <div
            ref={heroRef}
            className={`max-w-[1200px] mx-auto px-6 lg:px-8 ${
              heroInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <div className="max-w-[720px]">
              <h1 className="font-heading text-[2.25rem] sm:text-[3rem] lg:text-[3.5rem] leading-[1.08] font-bold text-stone-900 tracking-[-0.02em]">
                See how bikes.rentals works
              </h1>
              <p className="mt-6 text-[1.1rem] lg:text-[1.15rem] text-stone-500 leading-[1.7]">
                A practical walkthrough of booking, daily operations, delivery management, and issue documentation — built for real rental environments.
              </p>
            </div>
          </div>
        </section>

        <div className="border-t border-stone-200/40" />

        {/* Section 1 — Daily operations overview */}
        <Section
          title="Daily operations overview"
          placeholder="Screenshot placeholder – Admin dashboard (today view)"
        >
          <Paragraph>
            A live operational overview shows what's happening today — pickups, deliveries, returns, and active rentals.
          </Paragraph>
          <Paragraph className="mt-4 font-medium text-stone-700">
            Your team sees:
          </Paragraph>
          <BulletList
            items={[
              'Clear rental status',
              'What needs attention',
              'What has already been handled',
            ]}
          />
          <Emphasis>No parallel spreadsheets.</Emphasis>
          <Emphasis>No guessing what comes next.</Emphasis>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Section 2 — Issue & return management */}
        <Section
          title="Issue & return management"
          placeholder="Screenshot placeholder – Rental detail view with issue tracking"
        >
          <Paragraph>
            Rental issues are documented directly within each reservation.
          </Paragraph>
          <Paragraph className="mt-4 font-medium text-stone-700">
            For example:
          </Paragraph>
          <BulletList
            items={[
              'A customer reports a shifting problem at return',
              'A staff member inspects the bike',
              'The issue is confirmed and documented',
            ]}
          />
          <Paragraph>
            Affected bookings are immediately visible. Conflicts are shown, and swapping bikes in existing bookings is simple.
          </Paragraph>
          <Paragraph className="mt-3">
            Repair status is updated. Resolution becomes visible in the dashboard.
          </Paragraph>
          <Paragraph className="mt-4 font-medium text-stone-700">
            Each issue has a clear status:
          </Paragraph>
          <p className="text-[0.9rem] text-[#2d6a5a] font-medium mt-2 mb-5 tracking-wide">
            reported → inspected → confirmed → resolved
          </p>
          <Emphasis>No handwritten notes.</Emphasis>
          <Emphasis>No lost WhatsApp messages.</Emphasis>
          <Emphasis>No uncertainty about what was fixed and what wasn't.</Emphasis>
          <Paragraph className="mt-5">
            Every rental maintains a documented history of condition and actions taken.
          </Paragraph>
          <Paragraph className="mt-3">
            This reduces disputes, improves accountability, and keeps your fleet in control.
          </Paragraph>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Section 3 — Clean booking flow */}
        <Section
          title="Clean booking flow"
          placeholder="Screenshot placeholder – Booking frontend with date selection"
        >
          <Paragraph>
            Customers book through a guided flow designed to prevent mistakes before they reach your team.
          </Paragraph>
          <Paragraph className="mt-4 font-medium text-stone-700">
            They can:
          </Paragraph>
          <BulletList
            items={[
              'Select rental dates clearly',
              'Choose delivery windows if needed',
              'See final pricing before checkout',
              'Modify, extend, or cancel bookings themselves',
            ]}
          />
          <Paragraph>
            Clear booking reduces confusion before daily operations even begin.
          </Paragraph>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Section 4 — Delivery booking */}
        <Section
          title="Delivery booking built into checkout"
          placeholder="Screenshot placeholder – Delivery window selection and price calculation"
        >
          <Paragraph>
            Delivery options are selected directly during booking.
          </Paragraph>
          <BulletList
            items={[
              'Selectable delivery windows',
              'Automated delivery pricing based on real-time travel time',
              'Clear confirmation before checkout',
            ]}
          />
          <Emphasis>No manual price calculation.</Emphasis>
          <Emphasis>No back-and-forth about timing or cost.</Emphasis>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Section 5 — Operational analytics */}
        <Section
          title="Operational analytics"
          placeholder="Screenshot placeholder – Analytics dashboard"
        >
          <Paragraph>
            bikes.rentals provides operational insights that help you understand both performance and daily issues.
          </Paragraph>
          <Paragraph className="mt-4 font-medium text-stone-700">
            Key metrics include:
          </Paragraph>
          <BulletList
            items={[
              'Total revenue',
              'Total bookings',
              'Average booking value',
              'Active issues',
            ]}
          />
          <Paragraph className="mt-3 font-medium text-stone-700">
            Operators can also analyze:
          </Paragraph>
          <BulletList
            items={[
              'Revenue over time',
              'Revenue per asset',
              'Location performance',
              'Issues by product',
              'Blocking times by reason',
              'Booking status breakdown',
              'Payment methods',
            ]}
          />
          <Paragraph>
            Instead of exporting spreadsheets, your team can immediately see what is happening across bookings, inventory, and maintenance.
          </Paragraph>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Section 6 — Built for real rental days */}
        <section className="py-[70px] lg:py-[90px]">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
            <div className="max-w-[680px]">
              <h2 className="font-heading text-[1.6rem] lg:text-[2rem] font-bold text-stone-900 leading-[1.2] tracking-[-0.01em]">
                Built for real rental days
              </h2>
              <Paragraph className="mt-5">
                Whether you manage one location or several, bikes.rentals keeps booking, communication, delivery, and issue tracking aligned in one operational system.
              </Paragraph>
              <Emphasis>Not just a calendar.</Emphasis>
              <p className="text-[0.95rem] text-stone-700 font-medium leading-[1.7] mt-1">
                A working rental environment.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative py-[90px] lg:py-[110px] bg-[#2d6a5a] overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDMiLz48L3N2Zz4=')] opacity-50 pointer-events-none" />
          <div className="max-w-[1200px] mx-auto px-6 lg:px-8 relative z-10 flex flex-col items-center">
            <h2 className="font-heading text-[1.85rem] lg:text-[2.5rem] font-bold text-white leading-[1.15] max-w-[540px] text-center tracking-[-0.01em]">
              Ready to see how this fits your business?
            </h2>

            <Button
              asChild
              className="mt-9 bg-white text-[#2d6a5a] hover:bg-white/90 rounded-full px-7 h-12 text-[0.92rem] font-medium hover:shadow-lg"
              style={{ transition: 'background-color 0.2s, box-shadow 0.3s' }}
            >
              <Link to="/start-setup">
                Start your setup conversation
                <ArrowRight className="ml-2" size={17} />
              </Link>
            </Button>

            <div className="mt-4 space-y-0.5 text-center">
              <p className="text-[0.8rem] text-white/50">No commitment required.</p>
              <p className="text-[0.8rem] text-white/50">Typical setup call: 30 minutes.</p>
            </div>

            <Button
              variant="ghost"
              asChild
              className="mt-6 text-white/60 hover:text-white hover:bg-white/10 rounded-full px-5 h-10 text-[0.88rem] font-medium"
              style={{ transition: 'color 0.2s, background-color 0.2s' }}
            >
              <Link to="/request-demo">Request demo</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
